import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokerQuotes,
  brokers,
  contractualTerms,
  creditLine,
  insureds,
  policyholderFile,
  policyholders,
  products,
  quoteFinancial,
  settings,
  type Broker,
  type Insured,
  type Operation,
  type OperationInput,
  type Policyholder,
  type ProposalType,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import type { Money } from '@/shared/api/types';
import {
  applyRate,
  formatCpfCnpj,
  formatMoney,
  moneyInputError,
  moneyToInput,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Combobox } from '@/shared/ui/Combobox';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { statusLabel } from '@/shared/ui/statusLabels';

import { hojeEmBrasilia } from '../creditConsumption/labels';
import { fieldErrorOf } from '../shared/fieldError';
import { tiposDaAbertura } from './cotacao';
import {
  camposDoObjeto,
  conferirVigencia,
  grupoDeCreditoDaModalidade,
  limitesDoFim,
  limitesDoInicio,
  previaDoObjeto,
  produtosPossiveis,
  erroDaComissaoNoLimite,
  sugerirComissao,
  sugerirCorretor,
  sugerirEndereco,
  sugerirOpcaoUnica,
  sugerirPrazoDaEtapa,
  sugerirTaxa,
  sugerirValorGarantido,
  type Sugestao,
} from './sugestoesDaOperacao';

/** Campos que recebem sugestao na abertura. */
type Campo =
  | 'corretor'
  | 'endereco'
  | 'produto'
  | 'modalidade'
  | 'valor'
  | 'taxa'
  | 'comissao'
  | 'inicio'
  | 'prazo'
  | 'objeto'
  | 'pagamento';

type Digitados = Partial<Record<Campo, string>>;

/** Na edicao tudo ja foi digitado: nenhum campo recebe sugestao. */
function digitadosDaOperacao(operacao: Operation): Digitados {
  return {
    corretor: operacao.broker?.id ?? '',
    endereco: operacao.insured_address?.id ?? '',
    produto: operacao.product.id,
    modalidade: operacao.modality.id,
    valor: moneyToInput(operacao.insured_amount),
    taxa: rateToInput(operacao.applied_rate_bps),
    comissao: rateToInput(operacao.commission_bps),
    inicio: operacao.coverage_start?.slice(0, 10) ?? '',
    prazo: operacao.stage_due_at?.slice(0, 10) ?? '',
    objeto: operacao.subject,
  };
}

function semCampo(digitados: Digitados, campo: Campo): Digitados {
  const copia = { ...digitados };
  delete copia[campo];
  return copia;
}

/** Segunda linha das opcoes de parte: o documento e o nome fantasia. */
function descricaoDaParte(documento: string, fantasia = ''): string {
  return [formatCpfCnpj(documento), fantasia].filter(Boolean).join(' - ');
}

/**
 * Abertura e edicao de operacao.
 *
 * As listas de partes vem do servidor. O premio nao e um campo: ele e calculado a
 * partir do valor garantido e da taxa, e mostrado como previsao para que a pessoa
 * confira antes de gravar. O calculo definitivo continua sendo do servidor.
 *
 * Na abertura, o que ja se sabe chega preenchido e diz de onde veio (ADR 0103). O valor
 * de cada campo e o digitado ou, sem ele, a sugestao: a sugestao e calculada a cada
 * escolha, e por isso muda junto com ela, e nada que a pessoa digitou e trocado.
 */
export function OperationForm({
  operation,
  readOnly,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  operation?: Operation | undefined;
  readOnly?: boolean | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: OperationInput) => void;
  onCancel: () => void;
}) {
  const { session, can } = useAuth();
  const novo = !operation;
  const daCorretora = session?.broker_scoped === true;
  // O segurado (P-SEG-1) e da seguradora: a corretora nao le o cadastro de
  // segurados, e quem nao le nao recebe os dois campos. O pedido sem os campos
  // mantem o que esta gravado.
  const podeEscolherSegurado = !daCorretora && can('segurados.segurado.view');

  const [digitados, setDigitados] = useState<Digitados>(() =>
    operation ? digitadosDaOperacao(operation) : {},
  );
  const digitar = (campo: Campo, valor: string) =>
    setDigitados((atuais) => ({ ...atuais, [campo]: valor }));
  const valorDe = (campo: Campo, sugestao?: Sugestao) =>
    digitados[campo] ?? sugestao?.valor ?? '';
  const origemDe = (campo: Campo, sugestao?: Sugestao) =>
    digitados[campo] === undefined ? sugestao?.origem || undefined : undefined;

  const [hoje] = useState(hojeEmBrasilia);
  const [policyholderId, setPolicyholderId] = useState(
    operation?.policyholder.id ?? '',
  );
  const [tomadorEscolhido, setTomadorEscolhido] = useState<Policyholder | null>(null);
  const [corretorEscolhido, setCorretorEscolhido] = useState<Broker | null>(null);
  const [insuredId, setInsuredId] = useState(operation?.insured?.id ?? '');
  const [seguradoDaLista, setSeguradoDaLista] = useState<Insured | null>(null);
  const [fim, setFim] = useState(operation?.coverage_end?.slice(0, 10) ?? '');
  const [notas, setNotas] = useState(operation?.notes ?? '');
  const [valoresDasTags, setValoresDasTags] = useState<Record<string, string>>({});
  // Tipo da cotacao (P-COT-1): so na abertura. Endosso e renovacao como cotacao sao
  // decisao aberta (11.3 item 3 do plano 49); o tipo fica gravado e ainda nao muda
  // o caminho da operacao.
  const [tipo, setTipo] = useState<ProposalType>(operation?.proposal_type ?? 'apolice');

  // --- Segurado e endereco -------------------------------------------------------

  const [buscaSegurado, setBuscaSegurado] = useState('');
  const listaSegurados = useQuery({
    queryKey: ['insureds', 'options', buscaSegurado],
    queryFn: ({ signal }) => insureds.list({ limit: 100, q: buscaSegurado }, signal),
    enabled: podeEscolherSegurado,
    placeholderData: keepPreviousData,
  });
  const opcoesDeSegurado = useMemo(
    () =>
      (listaSegurados.data?.items ?? []).map((segurado) => ({
        value: segurado.id,
        label: segurado.legal_name,
        description: `${formatCpfCnpj(segurado.document)}${segurado.is_blocked ? ' - bloqueado' : ''}`,
      })),
    [listaSegurados.data],
  );
  const seguradoEscolhido = useQuery({
    queryKey: ['insured', insuredId],
    queryFn: ({ signal }) => insureds.get(insuredId, signal),
    enabled: podeEscolherSegurado && insuredId !== '',
  });
  const sugestaoEndereco =
    novo && seguradoEscolhido.data
      ? sugerirEndereco(seguradoEscolhido.data.addresses)
      : undefined;
  const insuredAddressId = valorDe('endereco', sugestaoEndereco);
  const opcoesDeEndereco = (seguradoEscolhido.data?.addresses ?? [])
    .filter((endereco) => endereco.active || endereco.id === insuredAddressId)
    .map((endereco) => ({
      value: endereco.id,
      label: `${endereco.label}${endereco.is_main ? ' (principal)' : ''}`,
    }));
  const enderecoGravado = operation?.insured_address;
  if (
    enderecoGravado &&
    insuredAddressId === enderecoGravado.id &&
    !opcoesDeEndereco.some((opcao) => opcao.value === enderecoGravado.id)
  ) {
    opcoesDeEndereco.unshift({
      value: enderecoGravado.id,
      label: enderecoGravado.label,
    });
  }
  const seguradoBloqueado =
    seguradoDaLista?.is_blocked ?? seguradoEscolhido.data?.is_blocked ?? false;

  // --- Tomador e corretor --------------------------------------------------------

  // A lista de tomadores vem do servidor pela busca, e nao inteira: o servidor
  // devolve no maximo 100 por consulta, e com mais tomadores ativos que isso a lista
  // fixa escondia os demais sem aviso (inventario 47, anexo C, item 18).
  const [buscaTomador, setBuscaTomador] = useState('');
  const listaTomadores = useQuery({
    queryKey: ['policyholders', 'options', buscaTomador],
    queryFn: ({ signal }) =>
      policyholders.list({ limit: 100, status: 'ativo', q: buscaTomador }, signal),
    placeholderData: keepPreviousData,
  });
  const opcoesDeTomador = useMemo(
    () =>
      (listaTomadores.data?.items ?? []).map((holder) => ({
        value: holder.id,
        label: holder.legal_name,
        description: descricaoDaParte(holder.document, holder.trade_name),
      })),
    [listaTomadores.data],
  );
  const tomadoresAlemDaLista =
    (listaTomadores.data?.total ?? 0) > (listaTomadores.data?.items.length ?? 0);

  // Os corretores tambem vem pela busca: com mais de 100 ativos, a lista fixa
  // escondia os demais sem aviso (inventario 47, anexo C, "Operacao e esteira" 18).
  const [buscaCorretor, setBuscaCorretor] = useState('');
  const listaCorretores = useQuery({
    queryKey: ['brokers', 'options', buscaCorretor],
    queryFn: ({ signal }) =>
      brokers.list({ limit: 100, stage: 'ativo', q: buscaCorretor }, signal),
    enabled: !daCorretora,
    placeholderData: keepPreviousData,
  });
  const opcoesDeCorretor = useMemo(
    () =>
      (listaCorretores.data?.items ?? []).map((broker) => ({
        value: broker.id,
        label: broker.legal_name,
        description: descricaoDaParte(broker.document, broker.trade_name),
      })),
    [listaCorretores.data],
  );
  const corretoresAlemDaLista =
    (listaCorretores.data?.total ?? 0) > (listaCorretores.data?.items.length ?? 0);

  // A corretora da sessao ou a do tomador: a lista traz so a pagina da busca, e a
  // comissao e a situacao precisam vir do cadastro.
  const idDoCorretorConhecido = daCorretora
    ? session?.broker?.id
    : (tomadorEscolhido?.broker_id ?? undefined);
  const corretorConhecido = useQuery({
    queryKey: ['brokers', 'detail', idDoCorretorConhecido],
    queryFn: ({ signal }) => brokers.get(idDoCorretorConhecido ?? '', signal),
    enabled: novo && Boolean(idDoCorretorConhecido),
  });
  const sugestaoCorretor =
    novo && !daCorretora && corretorConhecido.data
      ? sugerirCorretor(corretorConhecido.data)
      : undefined;
  const brokerId = valorDe('corretor', sugestaoCorretor);
  const corretorAtual = daCorretora
    ? corretorConhecido.data
    : [corretorEscolhido, corretorConhecido.data].find(
        (corretor) => corretor && corretor.id === brokerId,
      );

  // --- Produto e modalidade ------------------------------------------------------

  const listaProdutos = useQuery({
    queryKey: ['products'],
    queryFn: ({ signal }) => products.list(signal),
  });
  const possiveis = useMemo(
    () => produtosPossiveis(listaProdutos.data ?? []),
    [listaProdutos.data],
  );
  const sugestaoProduto = novo
    ? sugerirOpcaoUnica(possiveis, 'Sugerido: unico produto disponivel.')
    : undefined;
  const productId = valorDe('produto', sugestaoProduto);
  const produto = listaProdutos.data?.find((item) => item.id === productId);
  const opcoesDeProduto = (listaProdutos.data ?? []).filter(
    (item) => item.id === productId || possiveis.includes(item),
  );

  const modalidadesAtivas = (produto?.modalities ?? []).filter(
    (modalidade) => modalidade.status === 'ativo',
  );
  const sugestaoModalidade = novo
    ? sugerirOpcaoUnica(modalidadesAtivas, 'Sugerido: unica modalidade disponivel.')
    : undefined;
  const modalityId = valorDe('modalidade', sugestaoModalidade);
  const modalidades = (produto?.modalities ?? []).filter(
    (modalidade) => modalidade.status === 'ativo' || modalidade.id === modalityId,
  );
  const modalidade = modalidades.find((item) => item.id === modalityId);
  const grupo = grupoDeCreditoDaModalidade(produto, modalidade);

  // --- O que a analise deixou no tomador -----------------------------------------

  const limites = useQuery({
    queryKey: ['policyholder', policyholderId, 'credit-limits'],
    queryFn: ({ signal }) => policyholderFile.creditLimits(policyholderId, signal),
    enabled: novo && policyholderId !== '',
  });
  const limiteCalculado = useQuery({
    queryKey: ['policyholder', policyholderId, 'credit-line'],
    queryFn: ({ signal }) => creditLine.ofPolicyholder(policyholderId, signal),
    enabled: novo && policyholderId !== '',
  });
  const sugestaoValor =
    novo && modalidade && limites.data && !limiteCalculado.isPending
      ? sugerirValorGarantido(grupo, limites.data, limiteCalculado.data, daCorretora)
      : undefined;
  const sugestaoTaxa = novo
    ? sugerirTaxa(grupo, limites.data, produto?.branch_kind, modalidade)
    : undefined;

  // --- Vigencia, prazo, pagamento e objeto ---------------------------------------

  const sugestaoInicio: Sugestao | undefined = novo
    ? { valor: hoje, origem: 'Sugerido: data de hoje.' }
    : undefined;
  // O prazo por etapa so se le com a permissao da tela de prazos. Quem nao a tem ve o
  // campo em branco, e o servidor aplica o mesmo prazo na abertura.
  const prazosDaEmpresa = useQuery({
    queryKey: ['stage-slas'],
    queryFn: ({ signal }) => settings.stageSlas(signal),
    enabled: novo && !daCorretora && can('configuracoes.sla.view'),
  });
  const sugestaoPrazo =
    novo && prazosDaEmpresa.data
      ? sugerirPrazoDaEtapa(prazosDaEmpresa.data, hoje)
      : undefined;
  // Os limites da modalidade, como foram carregados. Sem a leitura, nada trava: o limite
  // desconhecido nao e limite zero.
  const limitesDaModalidade = useQuery({
    queryKey: ['modalities', modalityId, 'term-validation'],
    queryFn: ({ signal }) => quoteFinancial.termCheck(modalityId, null, signal),
    enabled: novo && modalityId !== '',
  });
  const limitesDaVigencia = limitesDaModalidade.data?.modality_limits;

  const opcoesDaCotacao = useQuery({
    queryKey: ['broker-quote-options'],
    queryFn: ({ signal }) => brokerQuotes.options(signal),
    enabled: novo,
  });
  const meiosDePagamento = opcoesDaCotacao.data?.payment_methods ?? [];
  const sugestaoPagamento = novo
    ? sugerirOpcaoUnica(meiosDePagamento, 'Sugerido: unica forma de pagamento ativa.')
    : undefined;
  const pagamento = valorDe('pagamento', sugestaoPagamento);
  // A comissao da corretora ou, sem ela, a padrao da empresa, que vem nas mesmas opcoes
  // que a corretora le (OPEN_QUESTIONS 2.97). A mesma regra do assistente da corretora.
  const sugestaoComissao =
    novo && corretorAtual
      ? sugerirComissao(
          corretorAtual,
          opcoesDaCotacao.data?.default_commission_bps,
          opcoesDaCotacao.data?.use_policyholder_commission === true
            ? tomadorEscolhido?.commission_bps
            : undefined,
        )
      : undefined;

  // O objeto que a abertura vai copiar da modalidade. Com ele, a minuta imprime esse
  // texto, e cada TAG vira um campo aqui: o valor vai para o bloco do objeto da cotacao,
  // o mesmo da tela de Objetos. Sem ele, a minuta imprime o campo Objeto da garantia.
  const objetoDaModalidade = useQuery({
    queryKey: ['operations', 'opening-object', modalityId],
    queryFn: ({ signal }) => contractualTerms.openingObject(modalityId, signal),
    enabled: novo && modalityId !== '',
  });
  const modeloDoObjeto =
    novo && objetoDaModalidade.data?.enabled ? objetoDaModalidade.data : undefined;
  const camposDasTags = modeloDoObjeto ? camposDoObjeto(modeloDoObjeto) : [];
  const enderecoEscolhido = seguradoEscolhido.data?.addresses.find(
    (endereco) => endereco.id === insuredAddressId,
  );
  const previa = modeloDoObjeto
    ? previaDoObjeto(modeloDoObjeto, valoresDasTags, {
        'tomador.nome': tomadorEscolhido?.legal_name ?? '',
        'segurado.nome':
          seguradoDaLista?.legal_name ?? seguradoEscolhido.data?.legal_name ?? '',
        'segurado.cidade': enderecoEscolhido?.city ?? '',
        'segurado.uf': enderecoEscolhido?.state ?? '',
      })
    : '';

  const valor = valorDe('valor', sugestaoValor);
  const taxa = valorDe('taxa', sugestaoTaxa);
  const comissao = valorDe('comissao', sugestaoComissao);
  const inicio = valorDe('inicio', sugestaoInicio);
  const prazo = valorDe('prazo', sugestaoPrazo);
  const objeto = modeloDoObjeto ? previa : valorDe('objeto');
  const vigencia = novo
    ? conferirVigencia(limitesDaVigencia, inicio, fim, hoje)
    : { inicio: undefined, fim: undefined };

  // Previa apenas. O premio definitivo e derivado no servidor, que e quem tem a
  // taxa acordada e a regra de arredondamento da operacao.
  const previsaoPremio: Money = applyRate(
    toMoney(valor || '0'),
    rateToBasisPoints(taxa || '0'),
  );

  const apiError = error instanceof ApiError ? error : null;
  const erroValor = moneyInputError(valor);
  const erroTaxa = rateInputError(taxa);
  const erroComissao =
    rateInputError(comissao) ??
    erroDaComissaoNoLimite(
      comissao,
      corretorAtual,
      opcoesDaCotacao.data?.commission_above_broker_cap,
    );
  const invalido =
    erroValor !== undefined ||
    erroTaxa !== undefined ||
    erroComissao !== undefined ||
    vigencia.inicio !== undefined ||
    vigencia.fim !== undefined;
  const semCadastroBase =
    (buscaTomador === '' && (listaTomadores.data?.items.length ?? 0) === 0) ||
    (listaProdutos.data?.length ?? 0) === 0;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (invalido) return;
    // O prazo sugerido nao vai no pedido: em branco, o servidor aplica o mesmo prazo e
    // conta o fim do dia no fuso de referencia. Vai so o que a pessoa digitou.
    const prazoDigitado = digitados.prazo ?? '';
    const valoresDoObjeto = Object.fromEntries(
      camposDasTags
        .map(({ chave }) => [chave, (valoresDasTags[chave] ?? '').trim()])
        .filter(([, valorDaTag]) => valorDaTag),
    ) as Record<string, string>;
    onSubmit({
      policyholder_id: policyholderId,
      broker_id: brokerId,
      product_id: productId,
      modality_id: modalityId,
      insured_amount: toMoney(valor || '0'),
      applied_rate_bps: rateToBasisPoints(taxa || '0'),
      commission_bps: rateToBasisPoints(comissao || '0'),
      coverage_start: inicio,
      coverage_end: fim,
      stage_due_at: prazoDigitado ? `${prazoDigitado}T23:59:59Z` : '',
      subject: objeto,
      notes: notas,
      ...(podeEscolherSegurado
        ? { insured_id: insuredId, insured_address_id: insuredAddressId }
        : {}),
      ...(operation ? {} : { proposal_type: tipo }),
      ...(modeloDoObjeto ? { object_tag_values: valoresDoObjeto } : {}),
      ...(novo && pagamento ? { payment_method_id: pagamento } : {}),
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {error.message}
        </Alert>
      ) : null}

      {semCadastroBase && !operation ? (
        <Alert tone="info" title="Cadastro basico incompleto">
          Uma operacao precisa de um tomador ativo, um produto e uma modalidade.
          Cadastre-os antes de abrir a primeira operacao.
        </Alert>
      ) : null}

      <fieldset disabled={readOnly || submitting} className="flex flex-col gap-5">
        {/* Tomador, produto e modalidade vem primeiro, por sugestao do dono: o limite
            e por grupo de modalidade, e com os tres o valor e a taxa ja saem preenchidos. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Combobox
              label="Tomador"
              required
              placeholder="Razao social, nome fantasia ou CNPJ"
              value={policyholderId}
              selectedLabel={
                tomadorEscolhido?.legal_name ?? operation?.policyholder.label ?? ''
              }
              options={opcoesDeTomador}
              loading={listaTomadores.isFetching}
              error={fieldErrorOf(apiError, 'policyholder_id')}
              hint={
                tomadoresAlemDaLista
                  ? `Mostrando ${listaTomadores.data?.items.length ?? 0} de ${listaTomadores.data?.total ?? 0} tomadores ativos. Use a busca para achar os demais.`
                  : undefined
              }
              emptyMessage="Nenhum tomador ativo encontrado."
              onSearch={setBuscaTomador}
              onChange={(opcao) => {
                setPolicyholderId(opcao?.value ?? '');
                setTomadorEscolhido(
                  listaTomadores.data?.items.find((item) => item.id === opcao?.value) ??
                    null,
                );
              }}
            />
          </div>
          <Select
            label="Produto"
            required
            placeholder="Selecione o produto"
            value={productId}
            error={fieldErrorOf(apiError, 'product_id')}
            hint={origemDe('produto', sugestaoProduto)}
            options={opcoesDeProduto.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            onChange={(event) =>
              // A modalidade escolhida era do produto anterior.
              setDigitados((atuais) => ({
                ...semCampo(atuais, 'modalidade'),
                produto: event.target.value,
              }))
            }
          />
          <Select
            label="Modalidade"
            required
            placeholder={
              productId ? 'Selecione a modalidade' : 'Escolha o produto primeiro'
            }
            value={modalityId}
            error={fieldErrorOf(apiError, 'modality_id')}
            hint={origemDe('modalidade', sugestaoModalidade)}
            options={modalidades.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
            onChange={(event) => digitar('modalidade', event.target.value)}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {/* No portal da corretora, a operacao nasce na corretora da sessao: o
              servidor ignora o corretor do pedido, e a tela nao o oferece. */}
          {!daCorretora ? (
            <Combobox
              label="Corretor"
              clearable
              placeholder="Sem corretor, operacao direta"
              value={brokerId}
              selectedLabel={
                corretorAtual?.legal_name ??
                (operation?.broker?.id === brokerId ? operation?.broker?.label : '') ??
                ''
              }
              options={opcoesDeCorretor}
              loading={listaCorretores.isFetching}
              hint={
                origemDe('corretor', sugestaoCorretor) ??
                (corretoresAlemDaLista
                  ? `Mostrando ${listaCorretores.data?.items.length ?? 0} de ${listaCorretores.data?.total ?? 0} corretores ativos. Use a busca para achar os demais.`
                  : 'Apenas corretores ativos aparecem aqui.')
              }
              emptyMessage="Nenhum corretor ativo encontrado."
              onSearch={setBuscaCorretor}
              onChange={(opcao) => {
                digitar('corretor', opcao?.value ?? '');
                setCorretorEscolhido(
                  listaCorretores.data?.items.find(
                    (item) => item.id === opcao?.value,
                  ) ?? null,
                );
              }}
            />
          ) : null}
          {!operation ? (
            <Select
              label="Tipo da cotacao"
              value={tipo}
              options={tiposDaAbertura.map((valorDoTipo) => ({
                value: valorDoTipo,
                label: statusLabel('tipoCotacao', valorDoTipo),
              }))}
              error={fieldErrorOf(apiError, 'proposal_type')}
              hint="Apolice ou renovacao. O endosso abre pelo Novo endosso da apolice. O tipo nao muda depois da abertura."
              onChange={(event) => setTipo(event.target.value as ProposalType)}
            />
          ) : null}
        </div>

        {podeEscolherSegurado ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Combobox
              label="Segurado"
              clearable
              placeholder="Razao social, nome fantasia, CPF ou CNPJ"
              value={insuredId}
              selectedLabel={
                seguradoDaLista?.legal_name ??
                (operation?.insured?.id === insuredId
                  ? operation?.insured?.label
                  : '') ??
                ''
              }
              options={opcoesDeSegurado}
              loading={listaSegurados.isFetching}
              error={fieldErrorOf(apiError, 'insured_id')}
              hint={
                seguradoBloqueado
                  ? 'Segurado bloqueado. O bloqueio fica registrado e ainda nao impede a operacao.'
                  : novo
                    ? 'A minuta imprime o segurado e o endereco dele.'
                    : undefined
              }
              emptyMessage="Nenhum segurado encontrado."
              onSearch={setBuscaSegurado}
              onChange={(opcao) => {
                setInsuredId(opcao?.value ?? '');
                setSeguradoDaLista(
                  listaSegurados.data?.items.find((item) => item.id === opcao?.value) ??
                    null,
                );
                // O endereco era do segurado anterior.
                setDigitados((atuais) => semCampo(atuais, 'endereco'));
              }}
            />
            <Select
              label="Endereco do segurado"
              placeholder={
                insuredId ? 'Selecione o endereco' : 'Escolha o segurado primeiro'
              }
              value={insuredAddressId}
              error={fieldErrorOf(apiError, 'insured_address_id')}
              hint={origemDe('endereco', sugestaoEndereco)}
              options={opcoesDeEndereco}
              onChange={(event) => digitar('endereco', event.target.value)}
            />
          </div>
        ) : operation?.insured ? (
          <p className="text-xs text-ink-600">
            Segurado: {operation.insured.label} (
            {formatCpfCnpj(operation.insured.document)})
            {operation.insured_address ? `, ${operation.insured_address.label}` : ''}
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-3">
          <MoneyField
            label="Valor garantido (R$)"
            required
            value={valor}
            error={erroValor ?? fieldErrorOf(apiError, 'insured_amount')}
            hint={origemDe('valor', sugestaoValor)}
            onValueChange={(texto) => digitar('valor', texto)}
          />
          <Field
            label="Taxa aplicada (%)"
            inputMode="decimal"
            value={taxa}
            error={erroTaxa ?? fieldErrorOf(apiError, 'applied_rate_bps')}
            hint={origemDe('taxa', sugestaoTaxa)}
            onChange={(event) => digitar('taxa', event.target.value)}
          />
          <Field
            label="Comissao (%)"
            inputMode="decimal"
            value={comissao}
            error={erroComissao ?? fieldErrorOf(apiError, 'commission_bps')}
            hint={origemDe('comissao', sugestaoComissao)}
            onChange={(event) => digitar('comissao', event.target.value)}
          />
        </div>

        <p className="rounded-md bg-brand-50 px-3 py-2 text-xs text-brand-700">
          Premio previsto:{' '}
          <span className="font-semibold tabular-nums">
            {formatMoney(previsaoPremio)}
          </span>
          . O valor definitivo e calculado pelo servidor a partir do valor garantido e
          da taxa aplicada.
        </p>

        {novo && meiosDePagamento.length > 0 ? (
          <Select
            label="Forma de pagamento"
            required
            placeholder="Selecione a forma de pagamento"
            value={pagamento}
            error={fieldErrorOf(apiError, 'payment_method_id')}
            hint={
              origemDe('pagamento', sugestaoPagamento) ??
              'A minuta imprime a forma de pagamento. As parcelas entram no financeiro da cotacao.'
            }
            options={meiosDePagamento.map((meio) => ({
              value: meio.id,
              label: meio.label,
            }))}
            onChange={(event) => digitar('pagamento', event.target.value)}
          />
        ) : null}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Inicio da vigencia"
            type="date"
            required={novo}
            value={inicio}
            error={vigencia.inicio ?? fieldErrorOf(apiError, 'coverage_start')}
            hint={
              [origemDe('inicio', sugestaoInicio), limitesDoInicio(limitesDaVigencia)]
                .filter(Boolean)
                .join(' ') || undefined
            }
            onChange={(event) => digitar('inicio', event.target.value)}
          />
          <Field
            label="Fim da vigencia"
            type="date"
            required={novo}
            value={fim}
            error={vigencia.fim ?? fieldErrorOf(apiError, 'coverage_end')}
            hint={
              novo
                ? `${limitesDoFim(limitesDaVigencia) || 'Sem padrao cadastrado.'} A minuta imprime este campo.`
                : undefined
            }
            onChange={(event) => setFim(event.target.value)}
          />
          <Field
            label="Prazo da etapa"
            type="date"
            value={prazo}
            hint={
              origemDe('prazo', sugestaoPrazo) ??
              (novo
                ? 'Em branco, vale o prazo da empresa para a etapa, quando houver.'
                : 'Usado para destacar operacoes atrasadas.')
            }
            onChange={(event) => digitar('prazo', event.target.value)}
          />
        </div>

        {modeloDoObjeto ? (
          <section
            aria-label="Campos do objeto"
            className="flex flex-col gap-3 rounded-md border border-line p-3"
          >
            {camposDasTags.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {camposDasTags.map((campo) =>
                  campo.tipo === 'editor_esteira' ? (
                    <Textarea
                      key={campo.chave}
                      label={campo.rotulo}
                      help={false}
                      required={campo.obrigatorio}
                      value={valoresDasTags[campo.chave] ?? ''}
                      error={fieldErrorOf(apiError, `tag_values.${campo.chave}`)}
                      onChange={(event) =>
                        setValoresDasTags((atuais) => ({
                          ...atuais,
                          [campo.chave]: event.target.value,
                        }))
                      }
                    />
                  ) : (
                    <Field
                      key={campo.chave}
                      label={campo.rotulo}
                      help={false}
                      required={campo.obrigatorio}
                      placeholder={campo.tipo === 'data' ? 'dd/mm/aaaa' : undefined}
                      value={valoresDasTags[campo.chave] ?? ''}
                      error={fieldErrorOf(apiError, `tag_values.${campo.chave}`)}
                      onChange={(event) =>
                        setValoresDasTags((atuais) => ({
                          ...atuais,
                          [campo.chave]: event.target.value,
                        }))
                      }
                    />
                  ),
                )}
              </div>
            ) : null}
            <Textarea
              label="Previa do objeto"
              readOnly
              rows={5}
              className="bg-surface-sunken"
              value={previa}
              hint="Texto padrao da modalidade com os campos acima: e o que a minuta imprime."
            />
          </section>
        ) : (
          <Textarea
            label="Objeto da garantia"
            required
            rows={3}
            value={objeto}
            error={fieldErrorOf(apiError, 'subject')}
            placeholder="Contrato, edital ou obrigacao garantida"
            onChange={(event) => digitar('objeto', event.target.value)}
          />
        )}

        <Textarea
          label="Observacoes"
          value={notas}
          onChange={(event) => setNotas(event.target.value)}
        />
      </fieldset>

      {!readOnly ? (
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={submitting} disabled={invalido}>
            {operation ? 'Salvar alteracoes' : 'Abrir operacao'}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
